<template>
  <div>
    <div :class="{ 'ring-offset-2 ring-primary-200 ring-4 rounded-md dark:ring-primary-800 dark:ring-offset-gray-900' : isActive }">
      <template v-if="isEnabled">
        <div class="w-full border px-3 py-1 rounded-md bg-gray-50 dark:bg-gray-900">
          <availability
            v-model:availabilitySlot="_availabilitySlot"
            v-model:availabilityMode="_availabilityMode"
            v-model:validation="_staging.validation"
            v-model:groupMode="_groupMode"
            v-model:loading="_loading"
            :services="staging.services"
            :presets="staging.presets"
            :waitlist="staging.waitlist"
            :override="staging.overrideTime"
            :customer="staging.customer"
            :move="staging.move"
          />

          <template v-if="allowRecurrence">
            <div v-if="!isMove" class="flex items-center pb-1 border-t mt-2 pt-2">
              <toggle-field v-model="_staging.recurrence.enabled" class="mr-3" />
              <div>
                <p class="text-gray-700 text-sm font-medium dark:text-gray-200">
                  {{ _staging.recurrence.enabled ? $t('components.reservation.create.availability.recurrenceEnabled') : $t('components.reservation.create.availability.recurrenceDisabled') }}
                </p>
                <p class="text-gray-600 text-sm dark:text-gray-300">
                  {{ $t('components.reservation.create.availability.repeat') }}
                </p>
              </div>
            </div>

            <div v-if="isMove" class="flex items-center pb-1 border-t mt-2 pt-2">
              <toggle-field v-model="_staging.recurrence.enabled" class="mr-3" />
              <div>
                <p class="text-gray-700 text-sm font-medium dark:text-gray-200">
                  {{ _staging.recurrence.enabled ? $t('components.reservation.create.availability.change') : $t('components.reservation.create.availability.dontChange') }}
                </p>
                <p class="text-gray-600 text-sm dark:text-gray-300">
                  {{ $t('components.reservation.create.availability.affectFuture') }}
                </p>
              </div>
            </div>
          </template>
        </div>
      </template>

      <template v-else>
        <div class="bg-gray-50 border text-center rounded p-4 dark:bg-gray-900">
          <p class="text-gray-600 text-sm dark:text-gray-300">
            {{ $t('components.reservation.create.availability.oneServiceComplete') }}
          </p>
        </div>
      </template>
    </div>

    <div class="flex items-center justify-end space-x-2 mt-6 mb-2">
      <!--<o-tooltip text="Show a preview of the schedule for the selected date and time">
        <o-button flat size="sm" :disabled="!_staging.customer || !isEnabled" @click="showPreview = !showPreview">
          {{ showPreview ? 'Hide' : 'Open' }} Preview
        </o-button>
      </o-tooltip>-->
      <o-tooltip v-if="isCustomerTypeSingle && $features.get('waitlist')" :text="$t('components.reservation.create.availability.waitlistTooltip')">
        <o-button flat size="sm" :disabled="!isCompleteCustomer || !isEnabled" @click="handleCreateWaitlist">
          {{ $t('components.reservation.create.availability.waitlist') }}
        </o-button>
      </o-tooltip>
    </div>

    <create-waitlist-modal
      ref="createWaitlistModal"
      @success="handleCreateWaitlistSuccess"
    />

    <Teleport v-if="showPreview" :to="'#' + panel.extension">
      <div class="h-full w-full max-w-sm flex-shrink-0 pr-2 overflow-hidden relative">
        <div class="bg-white rounded-xl shadow-lg h-full w-full overflow-hidden dark:bg-gray-900">
          <preview />
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import Availability from '@/components/reservation/elements/Availability.vue'
import Preview from '@/components/reservation/elements/Preview.vue'

import CreateWaitlistModal from '@/components/waitlist/actions/Create.vue'

import {
  ToggleField,
} from '@/components/fields'

const { $panel } = useNuxtApp()
const panel = inject<any>('panel')

interface Props {
  staging: any
  state: string
  isActive: boolean
  loading?: boolean
}

interface Emits {
  (e: 'update:staging', value: any): void
  (e: 'update:loading', value: boolean): void
}

const props = withDefaults(defineProps<Props>(), {
  loading: false,
})

const emit = defineEmits<Emits>()

const showPreview = ref(false)
const createWaitlistModal = ref<any>()

const _staging = computed({
  get () {
    return props.staging
  },
  set (value) {
    emit('update:staging', value)
  },
})

const _availabilitySlot = computed({
  get () {
    return props.staging.availabilitySlot
  },
  set (value) {
    if (value && value.services) {
      for (const service of value.services) {
        service.voucher = null
        service.customerPass = null
      }
    }

    _staging.value.availabilitySlot = value
  },
})

const _availabilityMode = computed({
  get () {
    return props.staging.overrideTime ? 'manual' : 'auto'
  },
  set (mode) {
    _staging.value.overrideTime = mode === 'manual'
  },
})

const _groupMode = computed(() => {
  return props.staging.groupMode
})

const _loading = computed({
  get () {
    return props.loading
  },
  set (value) {
    emit('update:loading', value)
  },
})

const allowRecurrence = computed(() => true)

const isEnabled = computed(() => {
  const services = props.staging.services
  const service = services[0]
  return !!service && !!service.service && (!!service.employee || service.employee === 0)
})

const isMove = computed(() => props.staging?.move)

const hasGroupService = computed(() => props.staging.hasGroupService)
const isCustomerTypeGroup = computed(() => props.staging.isCustomerTypeGroup)
const isCustomerTypeSingle = computed(() => props.staging.isCustomerTypeSingle)
const isCompleteCustomer = computed(() => props.staging.isCompleteCustomer)

function handleCreateWaitlist () {
  const customer = _staging.value.customer
  const services = _staging.value.services

  createWaitlistModal.value.open({ customer, services })
}

function handleCreateWaitlistSuccess (waitlist: any) {
  panel?.close()

  const path = '/waitlist/' + waitlist.idWaitlist
  $panel.create(path)
}
</script>
